import React from 'react';

const PrivacyPolicy = () => {
  const lastUpdated = "July 29, 2025";
  
  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="page-hero" style={{
        background: 'linear-gradient(135deg, var(--secondary-light) 0%, var(--white) 100%)',
        marginTop: '120px',
        padding: '4rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: 'var(--accent-blue)',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            paddingBottom: '0.5rem',
            borderBottom: '3px solid var(--accent-blue)',
            marginBottom: '1.5rem'
          }}>
            Legal
          </div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: '700',
            color: 'var(--primary-black)',
            marginBottom: '1rem'
          }}>
            Privacy Policy
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: 'var(--gray-600)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <p style={{
            fontSize: '0.9rem',
            color: 'var(--gray-600)',
            marginTop: '1rem'
          }}>
            <strong>Last Updated:</strong> {lastUpdated}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section" style={{ padding: '4rem 0' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          
          {/* Introduction */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Introduction</h2>
            <p style={styles.paragraph}>
              Bel-Aire Mechanical ("Company," "we," "us," or "our") respects your privacy and is committed to protecting 
              your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website <strong>belairemechanical.com</strong> (the "Site") or interact with our services.
            </p>
            <p style={styles.paragraph}>
              Please read this Privacy Policy carefully. By accessing or using our Site, you acknowledge that you have read, 
              understood, and agree to be bound by all the terms of this Privacy Policy. If you do not agree with our policies 
              and practices, please do not use our Site.
            </p>
          </div>

          {/* Information We Collect */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
            
            <h3 style={styles.subTitle}>2.1 Personal Information</h3>
            <p style={styles.paragraph}>
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul style={styles.list}>
              <li>Fill out contact forms or request information</li>
              <li>Submit employment applications</li>
              <li>Subscribe to our newsletter or communications</li>
              <li>Interact with us via email, phone, or other communication channels</li>
              <li>Request a quote or service information</li>
            </ul>
            <p style={styles.paragraph}>
              This information may include:
            </p>
            <ul style={styles.list}>
              <li>Name, email address, phone number, and mailing address</li>
              <li>Company name and job title</li>
              <li>Project details and service requirements</li>
              <li>Employment history and qualifications (for job applicants)</li>
            </ul>

            <h3 style={styles.subTitle}>2.2 Automatically Collected Information</h3>
            <p style={styles.paragraph}>
              When you visit our Site, we automatically collect certain information about your device and browsing activity, including:
            </p>
            <ul style={styles.list}>
              <li>IP address and geographic location (country/region level)</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring website addresses</li>
              <li>Pages visited on our Site and time spent on each page</li>
              <li>Date and time of your visit</li>
              <li>Device identifiers</li>
            </ul>
          </div>

          {/* Cookies and Tracking */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>3. Cookies and Tracking Technologies</h2>
            <p style={styles.paragraph}>
              We use cookies and similar tracking technologies to collect and track information about your browsing activity 
              on our Site. Cookies are small data files stored on your device that help us improve your experience.
            </p>
            
            <h3 style={styles.subTitle}>3.1 Types of Cookies We Use</h3>
            <div style={styles.table}>
              <div style={styles.tableRow}>
                <div style={styles.tableHeader}>Cookie Type</div>
                <div style={styles.tableHeader}>Purpose</div>
              </div>
              <div style={styles.tableRow}>
                <div style={styles.tableCell}><strong>Essential Cookies</strong></div>
                <div style={styles.tableCell}>Required for the Site to function properly. Cannot be disabled.</div>
              </div>
              <div style={styles.tableRow}>
                <div style={styles.tableCell}><strong>Analytics Cookies</strong></div>
                <div style={styles.tableCell}>Help us understand how visitors interact with our Site using Matomo Analytics.</div>
              </div>
              <div style={styles.tableRow}>
                <div style={styles.tableCell}><strong>Functional Cookies</strong></div>
                <div style={styles.tableCell}>Remember your preferences and settings for a better experience.</div>
              </div>
            </div>

            <h3 style={styles.subTitle}>3.2 Analytics (Matomo)</h3>
            <p style={styles.paragraph}>
              We use Matomo, a privacy-focused web analytics platform, to analyze website traffic and usage patterns. 
              Matomo collects anonymized data to help us understand how visitors use our Site so we can improve content 
              and user experience. Unlike some analytics platforms, Matomo is configured to respect your privacy:
            </p>
            <ul style={styles.list}>
              <li>Data is processed on servers we control</li>
              <li>IP addresses are anonymized</li>
              <li>We do not share analytics data with third parties</li>
              <li>You can opt-out of analytics tracking at any time</li>
            </ul>

            <h3 style={styles.subTitle}>3.3 Your Cookie Choices</h3>
            <p style={styles.paragraph}>
              When you first visit our Site, you will be presented with a cookie consent banner allowing you to accept 
              or decline non-essential cookies. You can change your cookie preferences at any time by:
            </p>
            <ul style={styles.list}>
              <li>Clearing your browser's cookies and revisiting the Site</li>
              <li>Adjusting your browser settings to block or delete cookies</li>
              <li>Using browser privacy/incognito mode</li>
            </ul>
            <p style={styles.paragraph}>
              Please note that disabling cookies may affect the functionality of certain features on our Site.
            </p>
          </div>

          {/* How We Use Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>4. How We Use Your Information</h2>
            <p style={styles.paragraph}>
              We use the information we collect for the following purposes:
            </p>
            <ul style={styles.list}>
              <li><strong>Provide Services:</strong> To respond to your inquiries, process requests, and deliver our services</li>
              <li><strong>Communication:</strong> To send you information you requested, updates, and marketing communications (with your consent)</li>
              <li><strong>Improve Our Site:</strong> To analyze usage patterns and enhance user experience</li>
              <li><strong>Employment:</strong> To evaluate job applications and communicate with candidates</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Security:</strong> To protect against fraud, unauthorized access, and other security threats</li>
              <li><strong>Business Operations:</strong> To manage our business relationships and operations</li>
            </ul>
          </div>

          {/* Information Sharing */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Information Sharing and Disclosure</h2>
            <p style={styles.paragraph}>
              We do not sell, trade, or rent your personal information to third parties. We may share your information 
              only in the following circumstances:
            </p>
            <ul style={styles.list}>
              <li><strong>Service Providers:</strong> With trusted vendors who assist us in operating our business (e.g., IT support, email services), subject to confidentiality agreements</li>
              <li><strong>Affiliates:</strong> With our parent company, Legence, and affiliated companies for business purposes</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Protection:</strong> To protect our rights, property, safety, or the safety of others</li>
            </ul>
          </div>

          {/* Data Security */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Data Security</h2>
            <p style={styles.paragraph}>
              We implement appropriate technical and organizational security measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul style={styles.list}>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure server infrastructure</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Employee training on data protection</li>
            </ul>
            <p style={styles.paragraph}>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive 
              to protect your personal information, we cannot guarantee absolute security.
            </p>
          </div>

          {/* Data Retention */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Data Retention</h2>
            <p style={styles.paragraph}>
              We retain your personal information only for as long as necessary to fulfill the purposes for which it 
              was collected, including to satisfy legal, accounting, or reporting requirements. The retention period 
              may vary depending on the context and our legal obligations.
            </p>
            <p style={styles.paragraph}>
              When personal information is no longer needed, we will securely delete or anonymize it in accordance 
              with our data retention policies.
            </p>
          </div>

          {/* Your Rights */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Your Rights and Choices</h2>
            <p style={styles.paragraph}>
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul style={styles.list}>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications or opt-out of analytics tracking</li>
              <li><strong>Portability:</strong> Request your data in a portable format (where applicable)</li>
            </ul>
            <p style={styles.paragraph}>
              To exercise any of these rights, please contact us using the information provided in Section 12 below.
            </p>
          </div>

          {/* Third-Party Links */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Third-Party Links</h2>
            <p style={styles.paragraph}>
              Our Site may contain links to third-party websites, services, or applications that are not operated by us. 
              This Privacy Policy does not apply to third-party sites. We encourage you to review the privacy policies 
              of any third-party sites you visit.
            </p>
          </div>

          {/* Children's Privacy */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Children's Privacy</h2>
            <p style={styles.paragraph}>
              Our Site is not intended for children under the age of 13 (or 16 in certain jurisdictions). We do not 
              knowingly collect personal information from children. If you believe we have collected information from 
              a child, please contact us immediately so we can delete such information.
            </p>
          </div>

          {/* Changes to Policy */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>11. Changes to This Privacy Policy</h2>
            <p style={styles.paragraph}>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, 
              operational, or regulatory reasons. When we make material changes, we will:
            </p>
            <ul style={styles.list}>
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>Post the revised policy on our Site</li>
              <li>Notify you via email or prominent notice on our Site (for significant changes)</li>
            </ul>
            <p style={styles.paragraph}>
              We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
            </p>
          </div>

          {/* Contact Information */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>12. Contact Us</h2>
            <p style={styles.paragraph}>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div style={styles.contactBox}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: 'var(--primary-black)' }}>
                Bel-Aire Mechanical
              </p>
              <p style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-600)' }}>
                4201 N 47th Avenue<br />
                Phoenix, AZ 85031
              </p>
              <p style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-600)' }}>
                <strong>Phone:</strong> (623) 846-8600
              </p>
              <p style={{ margin: '0', color: 'var(--gray-600)' }}>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@belairemechanical.com" style={{ color: 'var(--accent-blue)' }}>
                  privacy@belairemechanical.com
                </a>
              </p>
            </div>
          </div>

          {/* Acknowledgment */}
          <div style={{
            marginTop: '3rem',
            padding: '2rem',
            background: 'var(--secondary-light)',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.95rem' }}>
              By using our website, you acknowledge that you have read and understood this Privacy Policy 
              and agree to its terms.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

const styles = {
  section: {
    marginBottom: '2.5rem',
    paddingBottom: '2rem',
    borderBottom: '1px solid var(--gray-200)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--primary-black)',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderLeft: '4px solid var(--accent-blue)',
    paddingLeft: '1rem',
  },
  subTitle: {
    fontSize: '1.15rem',
    fontWeight: '600',
    color: 'var(--primary-black)',
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
  },
  paragraph: {
    fontSize: '1rem',
    color: 'var(--gray-600)',
    lineHeight: '1.7',
    marginBottom: '1rem',
  },
  list: {
    marginBottom: '1rem',
    paddingLeft: '1.5rem',
    color: 'var(--gray-600)',
    lineHeight: '1.8',
  },
  table: {
    marginTop: '1rem',
    marginBottom: '1.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    borderBottom: '1px solid var(--gray-200)',
  },
  tableHeader: {
    padding: '0.75rem 1rem',
    background: 'var(--accent-blue)',
    color: 'var(--white)',
    fontWeight: '600',
    fontSize: '0.9rem',
  },
  tableCell: {
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    color: 'var(--gray-600)',
    borderRight: '1px solid var(--gray-200)',
  },
  contactBox: {
    marginTop: '1rem',
    padding: '1.5rem',
    background: 'var(--white)',
    border: '1px solid var(--gray-300)',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
};

export default PrivacyPolicy;
